import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ChevronDown, Plus, Receipt, Route, User } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';

/**
 * Fixed page-level create menu. Not a dashboard widget — it stays in the
 * greeting header regardless of which widgets are enabled.
 */
export function DashboardNewMenu({ clinicId }: { clinicId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const items = [
    clinicId && hasPermission(clinicId, 'patients', 'create')
      ? {
          to: '/patients/new',
          label: t('dashboard.new_patient', 'Ny patient'),
          icon: User,
        }
      : null,
    clinicId && hasPermission(clinicId, 'appointments', 'create')
      ? {
          to: '/appointments',
          label: t('dashboard.new_appointment', 'Ny aftale'),
          icon: CalendarPlus,
        }
      : null,
    clinicId && hasPermission(clinicId, 'appointments', 'create')
      ? {
          to: '/appointments?bookCourse=1',
          label: t('treatment_courses.book_title'),
          icon: Route,
        }
      : null,
    clinicId && hasPermission(clinicId, 'invoices', 'create')
      ? {
          to: '/invoices/new',
          label: t('dashboard.new_invoice', 'Ny faktura'),
          icon: Receipt,
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item != null);

  if (items.length === 0) return null;

  return (
    <MenuPopover
      align="end"
      title={t('dashboard.new_menu', 'Ny')}
      trigger={
        <Button type="button" variant="primary">
          <Plus />
          {t('dashboard.new_menu', 'Ny')}
          <ChevronDown />
        </Button>
      }
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <MenuItem key={item.to} icon={<Icon className="h-5 w-5" />} onClick={() => navigate(item.to)}>
            {item.label}
          </MenuItem>
        );
      })}
    </MenuPopover>
  );
}
